import { useAuth } from "../../lib/auth";
import { useState } from "react";
import { Plus, Eye, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useApi, Loading, Panel, inputCls, btn, Empty } from "../../components/kit";

const LABEL = { required: "Required", optional: "Optional", planned: "Planned", na: "N/A" };

function Roles({ roles, reload }) {
  const [name, setName] = useState("");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/roles", { name }); setName(""); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const patch = async (r, body) => { try { await api.patch(`/roles/${r.id}`, body); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Panel title="Roles" testid="roles-panel">
      <form onSubmit={add} className="mb-4 flex gap-2"><input className={inputCls} placeholder="New role, e.g. Packaging Operator" aria-label="New role" value={name} onChange={(e) => setName(e.target.value)} data-testid="role-name-input" /><button className={btn.dark} data-testid="role-add-button"><Plus className="h-4 w-4" /> Add</button></form>
      {roles.length === 0 ? <Empty>No roles yet. Add DCBC's real roles here or bring them in with the CSV import.</Empty> : (
        <ul className="divide-y">{roles.map((r) => (
          <li key={r.id} className="flex items-center gap-2 py-2" data-testid={`role-${r.id}`}>
            <input className={`${inputCls} ${r.retired ? "line-through text-muted-foreground" : ""}`} defaultValue={r.name} aria-label="Role name" onBlur={(e) => e.target.value !== r.name && patch(r, { name: e.target.value })} />
            <button className={btn.ghost} onClick={() => patch(r, { retired: !r.retired })} data-testid={`role-retire-${r.id}`}>{r.retired ? "Restore" : "Retire"}</button>
          </li>))}</ul>)}
    </Panel>
  );
}

function Coverage() {
  const { data, loading } = useApi("/course-map/coverage");
  if (loading) return <Loading />;
  const cols = [["completed", "Complete", "text-emerald-700"], ["in_progress", "In progress", "text-amber-700"], ["awaiting_signoff", "Awaiting sign-off", "text-sky-700"], ["overdue", "Overdue", "text-rose-700"], ["not_started", "Not started", "text-slate-600"]];
  return (
    <Panel title="Coverage by role" testid="coverage-panel">
      {data.length === 0 && <Empty>No active roles.</Empty>}
      <div className="space-y-5">{data.map((r) => (
        <div key={r.role.id}>
          <p className="font-semibold">{r.role.name} <span className="text-sm font-normal text-muted-foreground">· {r.headcount} people</span></p>
          {r.courses.length === 0 ? <p className="text-sm text-muted-foreground">No required courses.</p> : r.courses.map((c) => (
            <div key={c.course_id} className="mt-2 rounded-lg border bg-white p-3">
              <p className="text-sm font-medium mb-2">{c.title}</p>
              <div className="grid gap-2 sm:grid-cols-5">{cols.map(([k, l, tone]) => (
                <div key={k}><p className={`text-xs font-semibold uppercase ${tone}`}>{l} · {c.people[k].length}</p><p className="text-xs text-muted-foreground">{c.people[k].join(", ") || "—"}</p></div>))}</div>
            </div>))}
        </div>))}</div>
    </Panel>
  );
}

export default function CourseMap() {
  const { data, loading, reload } = useApi("/course-map");
  const { user } = useAuth();
  const [changes, setChanges] = useState({});
  const [preview, setPreview] = useState(null);
  if (loading) return <Loading />;
  const cell = (r, c) => changes[`${r}:${c}`] || data.cells.find((x) => x.role_id === r && x.course_id === c) || { requirement: "na", due_days: 30 };
  const change = (r, c, patch) => { setPreview(null); setChanges({ ...changes, [`${r}:${c}`]: { ...cell(r, c), role_id: r, course_id: c, ...patch } }); };
  const list = Object.values(changes);
  const doPreview = async () => { try { const r = await api.post("/course-map/preview", { changes: list }); setPreview(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const save = async () => {
    try { const r = await api.put("/course-map", { changes: list }); toast.success(`Saved. ${r.data.assignments_created} new assignment(s) created.`); setChanges({}); setPreview(null); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const roles = data.roles.filter((r) => !r.retired);
  return (
    <div className="space-y-6">
      {user.is_superadmin && <Roles roles={data.roles} reload={reload} />}
      <Panel title="Course map" testid="course-map-panel">
        <p className="mb-4 text-sm text-muted-foreground">Required courses are assigned automatically to everyone with the role, counting days from their start date or the day they gained the role (whichever is later). Draft courses can only be “Planned” — visible to you alone.</p>
        {roles.length === 0 || data.courses.length === 0 ? <Empty>Add roles and courses first.</Empty> : (
          <div className="overflow-x-auto"><table className="text-sm" data-testid="course-map-table">
            <thead><tr><th className="p-2 text-left sticky left-0 bg-card">Role</th>{data.courses.map((c) => <th key={c.id} className="p-2 text-left min-w-[180px] align-bottom"><span className="font-semibold">{c.title}</span><span className="block text-xs font-normal text-muted-foreground">{c.published_version_id ? "Published" : "Draft"}</span></th>)}</tr></thead>
            <tbody>{roles.map((r) => (
              <tr key={r.id} className="border-t"><th className="p-2 text-left sticky left-0 bg-card font-semibold">{r.name}</th>
                {data.courses.map((c) => { const v = cell(r.id, c.id); const dirty = !!changes[`${r.id}:${c.id}`]; return (
                  <td key={c.id} className={`p-2 align-top ${dirty ? "bg-amber-50" : ""}`}>
                    <select aria-label={`${r.name} — ${c.title}`} className="w-full rounded-md border bg-white p-2" value={v.requirement} onChange={(e) => change(r.id, c.id, { requirement: e.target.value })} data-testid={`map-${r.id}-${c.id}`}>
                      {Object.entries(LABEL).map(([k, l]) => <option key={k} value={k} disabled={k === "required" && !c.published_version_id}>{l}</option>)}</select>
                    {v.requirement === "required" && <label className="mt-1 flex items-center gap-1 text-xs">within <input type="number" min="0" max="730" className="w-16 rounded border p-1" value={v.due_days} onChange={(e) => change(r.id, c.id, { due_days: Number(e.target.value) })} data-testid={`map-days-${r.id}-${c.id}`} /> days of start</label>}
                  </td>); })}
              </tr>))}</tbody></table></div>
        )}
        {list.length > 0 && (
          <div className="mt-4 space-y-3 rounded-xl border bg-muted/40 p-4" data-testid="map-changes">
            <p className="font-semibold">{list.length} unsaved change(s)</p>
            {preview && <ul className="text-sm space-y-1" data-testid="map-preview">{preview.changes.map((p, i) => {
              const rn = data.roles.find((x) => x.id === p.role_id)?.name; const cn = data.courses.find((x) => x.id === p.course_id)?.title;
              return <li key={i}>{rn} → {cn}: <b>{LABEL[p.requirement]}</b>{p.requirement === "required" && ` — ${p.people_affected} people will get this course`}</li>; })}</ul>}
            <div className="flex flex-wrap gap-2">
              <button className={btn.ghost} onClick={doPreview} data-testid="map-preview-button"><Eye className="h-4 w-4" /> Preview</button>
              <button className={btn.primary} onClick={save} disabled={!preview} data-testid="map-save-button"><Save className="h-4 w-4" /> Save changes</button>
              <button className={btn.ghost} onClick={() => { setChanges({}); setPreview(null); }}>Discard</button>
            </div>
          </div>
        )}
      </Panel>
      <Coverage />
    </div>
  );
}
